import {
  Directive,
  ElementRef,
  forwardRef,
  OnDestroy,
  OnInit,
  Renderer2,
  inject,
  ViewContainerRef,
  Injector,
  input,
  ComponentRef,
  DestroyRef,
} from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { AbstractControlValueAccessor } from '../abstract_control_value_accessor.abstract';
import { Observable, map, take, combineLatest, ReplaySubject } from 'rxjs';
import intlTelInput, { Iti } from 'intl-tel-input';
import { IconComponent } from '@app/shared/components/icon/icon.component';
import { SettingsService } from '@app/core/settings/settings.service';
import { MSISDN_INPUT_WRAPPER_ID_PREFIX } from '@app/shared/services/msisdn-input-id.service';
import { Iso2 } from 'intl-tel-input/data';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const utilsLoaded = new ReplaySubject<void>(1);

const BRANDED_CONTROL_PADDING_LEFT_PX = -4;
const CONTROL_PADDING_LEFT_PX = 8;

@Directive({
  selector: '[appMsisdnInput]',
  host: {
    '(input)': 'handleInput()',
    '(countrychange)': 'countryChange()',
    '(blur)': 'onTouched()',
  },
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => MsisdnInputDirective), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => MsisdnInputDirective), multi: true },
  ],
})
export class MsisdnInputDirective extends AbstractControlValueAccessor implements OnInit, OnDestroy, Validator {
  public readonly controlUID = input.required<number>();
  public readonly autoPrependWithPlus = input<boolean>(false);
  public readonly branded = input<boolean>(false);
  public readonly autoPrependSuggestedMsisdnCountry = input<boolean>(true);

  private msisdnInput?: Iti;
  private validationNumber?: string;
  private originalValue: string | null = null;

  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly settingsService = inject(SettingsService);
  private readonly elementRef = inject(ElementRef<HTMLInputElement>);
  private readonly renderer = inject(Renderer2);

  public ngOnInit(): void {
    this.settingsService.getSettingsOnce()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((settings) => {
        this.msisdnInput = intlTelInput(this.elementRef.nativeElement,
          this.intlTelInputOptions(
            settings.suggestedMsisdnCountry(),
            settings.availableMsisdnCountries()),
        );

        setTimeout(() => this.updateSelectedDialCode());
      });
  }

  public ngOnDestroy(): void {
    this.msisdnInput?.destroy();
    this.msisdnInput = undefined;
  }

  public registerOnChange(fn: (value: string) => void): void {
    super.registerOnChange(fn);
    this.plainNumber().pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(
      number => {
        if (this.originalValue !== number) {
          this.onChange(number);
        }
      },
    );
  }

  public handleInput(): void {
    this.plainNumber().pipe(
      take(1),
    ).subscribe(number => this.onChange(number),
    );
  }

  public countryChange(): void {
    this.handleInput();
    this.updateSelectedDialCode();
  }

  public writeValue(value: string | null): void {
    this.originalValue = value;
    const normalizedValue = value == null || value === '' ? '' : value;
    this.setInputValue(normalizedValue);
  }

  public validate(_: AbstractControl): ValidationErrors | null {
    const number = this.validationNumber;
    if (!number) { return null; }
    return !this.msisdnInput || this.msisdnInput.isValidNumber()
      ? null
      : { error: this.msisdnInput.getValidationError() };
  }

  private setInputValue(value: string): void {
    combineLatest([
      this.settingsService.getSuggestedMsisdnCountry(),
      utilsLoaded,
    ])
    .pipe(
      takeUntilDestroyed(this.destroyRef),
    )
    .subscribe(([suggestedMsisdnCountry]) => {
      if (!this.msisdnInput) {
        return;
      }

      const correctValue = value && !value?.startsWith?.('+') ? `+${value}` : value;
      this.msisdnInput.setNumber(correctValue);

      if (!this.msisdnInput.isValidNumber()) {
        let countryIso = this.msisdnInput.getSelectedCountryData().iso2 ?? '';
        if (this.autoPrependSuggestedMsisdnCountry()) {
          countryIso = (suggestedMsisdnCountry ?? '') as Iso2;
        }
        this.msisdnInput.setCountry((countryIso ?? '') as Iso2);
      }

      const regex = `^\\+?${value}`;
      this.msisdnInput.setNumber(value.replace(regex, ''));
    });
  }

  private intlTelInputOptions(suggestedMsisdnCountry: string | null, availableMsisdnCountries: string[]): any {
    return {
      autoPlaceholder: false,
      initialCountry: suggestedMsisdnCountry,
      ...suggestedMsisdnCountry ? { countryOrder: [suggestedMsisdnCountry] } : {},
      showFlags: false,
      countrySearch: false,
      onlyCountries: availableMsisdnCountries,
      separateDialCode: true,
      loadUtils: () => import('@app/../../node_modules/intl-tel-input/build/js/utils.js').finally(() =>
        setTimeout(() => utilsLoaded.next()),
      ),
    };
  }

  private plainNumber(): Observable<string | null> {
    return utilsLoaded.pipe(
      map(() => this.msisdnInput?.getNumber(0)),
      map(number => {
        this.validationNumber = number;

        if (!number) {
          return null;
        }
        if (number.startsWith('+', 0) && !this.autoPrependWithPlus()) {
          return number.slice(1);
        } else if (!number.startsWith('+', 0) && this.autoPrependWithPlus()) {
          return `+${number}`;
        }
        return number;
      }),
    );
  }

  private updateSelectedDialCode(): void {
    setTimeout(() => {
      const selectedElement = this.getMsisdnElement('iti__selected-country-primary');
      if (!selectedElement) {
        return;
      }

      const dialCodeElement = this.getMsisdnElement('iti__selected-dial-code');

      this.renderer.setProperty(selectedElement, 'innerHTML', '');

      this.createDialCodeElement(selectedElement, dialCodeElement);
    });
  }

  private createDialCodeElement(parentElement: Element, dialCodeElement: Element | undefined | null): void {
    this.settingsService.getSuggestedMsisdnCountryDialCode().pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(suggestedMsisdnCountryDialCode => {
      if (dialCodeElement) {
        if (!dialCodeElement.innerHTML) {
          const text = this.renderer.createText(`+${suggestedMsisdnCountryDialCode}`);
          this.renderer.appendChild(dialCodeElement, text);
        }
        this.renderer.appendChild(parentElement, dialCodeElement);
      }

      this.renderer.appendChild(parentElement, this.createChevronIcon().location.nativeElement);

      const elementPaddingLeft = `${(parentElement?.scrollWidth ?? 0) + (this.branded() ? BRANDED_CONTROL_PADDING_LEFT_PX : CONTROL_PADDING_LEFT_PX)}px`;
      this.renderer.setStyle(this.elementRef.nativeElement, 'padding-left', elementPaddingLeft);
    });
  }

  private createChevronIcon(): ComponentRef<IconComponent> {
    const iconRef = this.viewContainerRef.createComponent(IconComponent, {
      injector: this.injector,
    });

    iconRef.setInput('name', 'chevron-down');
    iconRef.setInput('size', 'sm');

    const classList = [
      'tw-ml-spacing-50',
      ...this.branded() ? ['tw-text-component-input-text'] : [],
    ];
    iconRef.location.nativeElement.classList.add(...classList);

    return iconRef;
  }

  private getMsisdnElement(className: string): Element | null {
    return document.getElementById(`${MSISDN_INPUT_WRAPPER_ID_PREFIX}-${this.controlUID()}`)?.querySelector(`.${className}`) ?? null;
  }
}
